
import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Card, CardActions, CardContent, CardHeader, Divider, Grid, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { LayoutDashboard } from '../layouts/DasboardLayout';
import { useAuthentication } from '../hooks/useAuthentication';
import { municipiosLocalidades } from '../constants/MunicipiosLocalidad';
import { API_URL } from '../config';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import FolioDialog from '../components/Dialogs/FolioDialog';
import ScrollbarContainer from '../components/ScrollBar/CustomScrollbar';

const initialValues = {
  name: '',
  address: '',
  phone: '',
  birthdate: '',
  curp: '',
  municipio: '',
  sexo: '',
  user: '',
  otroMunicipio: '',
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('El nombre es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(50, 'El nombre no debe exceder los 50 caracteres'),

  address: Yup.string().required('La dirección es requerida'),

  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'El teléfono debe ser un número de 10 dígitos'),

  birthdate: Yup.string().when('curp', (curp, schema) => {
    if (curp && typeof curp === 'string' && curp.trim().length === 18) {
      return schema.required('La fecha de nacimiento es requerida');
    }
    return schema;
  }),

  curp: Yup.string()
    .transform((value) => (value ? value.toUpperCase().replace(/\s/g, '').trim() : ''))
    .matches(/^[A-Z0-9]+$/, 'La CURP solo debe contener letras y números')
    .matches(
      /^[A-Z]{4}\d{6}[HM]{1}[A-Z]{5}[A-Z0-9]{2}$/,
      'La CURP no tiene un formato válido'
    )
    .required('La CURP es requerida'),

  municipio: Yup.string().required('El municipio es requerido'),

  sexo: Yup.string().required('El género es requerido'),
});

const HomePage = () => {
  const { idUser } = useAuthentication();
  const [formSend, setFormSend] = useState(false);
  const [dataResponse, setDataResponse] = useState(null);
  const [municipios, setMunicipios] = useState([]);
  const [validos, setValidos] = useState(false);
  const [nombre, setNombre] = useState('');
  const [configuracion, setConfiguracion] = useState(null);
  const [users, setUsers] = useState([]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      values.user = idUser;
      values.municipio = municipios.find((m) => m.value === values.municipio).label;
      try {
        const response = await fetch(`${API_URL}/person/register`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify(values),
          }
        );
        const data = await response.json();
        if (!response.ok) {
          const errorData = await response.json();
          toast.error(errorData.message);
          return;
        }
        setDataResponse(data);
        setFormSend(true);
        toast.success('Registro guardado correctamente');
      } catch (error) {
        console.error(error);
        toast.error('Error al guardar el registro');
      }
    }
  });

  //Funcion para obtener unicamnete los municipios
  const getMunicipios = () => {
    const municipios = municipiosLocalidades.reduce((acc, item) => {
      if (!acc.find((element) => element.value === item.claveMunicipio)) {
        acc.push({
          value: item.claveMunicipio,
          label: item.municipio,
        });
      }
      return acc;
    }, []);

    // Ordenar por nombre correctamente usando label
    municipios.sort((a, b) => a.label.localeCompare(b.label));

    // Agregar la opcion de otro municipio
    municipios.push({
      value: 'otro',
      label: 'Otro estado',
    });

    setMunicipios(municipios);
  };

  const validarCurp = (curp) => {
    if (curp.length === 18) {
      const año = parseInt(curp.substring(4, 6), 10);
      const mes = curp.substring(6, 8);
      const dia = curp.substring(8, 10);
      const siglo = año < 30 ? 2000 : 1900;
      const fechaFormato = `${siglo + año}-${mes}-${dia}`;
      formik.setFieldValue('birthdate', fechaFormato);
      formik.setFieldValue('sexo', curp.substring(10, 11) === 'H' ? 'Hombre' : 'Mujer');
      setValidos(false);
    }
    setValidos(false);
  };

  const handleShowFormikErrors = () => {
    Object.keys(formik.errors).forEach((key) => {
      toast.error(formik.errors[key]);
    });
  };

  const getConfiguracion = async () => {
    try {
      const response = await fetch(
        `${API_URL}/configuracion/info`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        setConfiguracion(data);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error fetching configuracion:', error);
      toast.error('Error al obtener la configuración');
    }
  };

  const getUserFolio = async () => {
    try {
      const response = await fetch(`${API_URL}/person/search/by/name/?name=${nombre}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        toast.error(data.message);
        return;
      }
      setUsers(data);

    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Error al obtener el usuario');
    }
  };

  useEffect(() => {
    validarCurp(formik.values.curp);
    // eslint-disable-next-line
  }, [formik.values.curp]);

  useEffect(() => {
    getMunicipios();
    getConfiguracion();
    //Colocar fecha de ejemplo en el formik.values.birthdate
    formik.setFieldValue('birthdate', '1999-12-31');
    // eslint-disable-next-line
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        p: 2,
      }}
    >
      <Card
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          flexDirection: 'column',
          borderRadius: '20px',
          marginTop: 4,
        }}
      >
        <CardContent
          sx={{
            width: '100%',
          }}
        >
          <ScrollbarContainer>
            <Typography
              variant="h4"
              align="center"
              color="textPrimary"
              gutterBottom
              fontWeight={700}
            >
              Formulario de Registro {configuracion?.nombre || ''}
            </Typography>
            <Typography
              variant="body1"
              color="textPrimary"
              gutterBottom
            >
              Llena los campos para registrar a una persona.
            </Typography>
            <Divider sx={{ mt: 2, mb: 2 }} />
            <Grid
              container
              spacing={2}
            >
              {municipios?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={municipios ?? []}
                    getOptionLabel={(option) => option?.label || ""}
                    value={municipios.find((m) => m.value === formik.values.municipio) ?? null}
                    onChange={(_, newValue) => {
                      formik.setFieldValue("municipio", newValue?.value || "");
                    }}
                    onBlur={formik.handleBlur}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Municipio"
                        name="municipio"
                        error={formik.touched.municipio && Boolean(formik.errors.municipio)}
                        helperText={formik.touched.municipio && formik.errors.municipio}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              )}
              {formik.values.municipio === 'otro' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Otro estado"
                    name="otroMunicipio"
                    value={formik.values.otroMunicipio}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.otroMunicipio && Boolean(formik.errors.otroMunicipio)}
                    helperText={formik.touched.otroMunicipio && formik.errors.otroMunicipio}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  required
                  label="Nombre completo"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="CURP"
                  name="curp"
                  value={formik.values.curp}
                  onChange={(e) => {
                    const curpLimpia = e.target.value
                      .toUpperCase() // Convierte a mayúsculas
                      .replace(/\s+/g, '') // Elimina espacios en blanco, incluyendo tabulaciones
                      .replace(/[^A-Z0-9]/g, '') // Elimina caracteres especiales
                      .trim(); // Elimina espacios extra al inicio y al final
                    formik.setFieldValue('curp', curpLimpia);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.curp && Boolean(formik.errors.curp)}
                  helperText={formik.touched.curp && formik.errors.curp}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="phone"
                  type='number'
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  required
                  disabled={validos}
                  label="Género"
                  name="sexo"
                  value={formik.values.sexo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.sexo && Boolean(formik.errors.sexo)}
                  helperText={formik.touched.sexo && formik.errors.sexo}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  select
                >
                  <MenuItem value="Mujer">Mujer</MenuItem>
                  <MenuItem value="Hombre">Hombre</MenuItem>
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  disabled={validos}
                  label="Fecha de nacimiento"
                  name="birthdate"
                  value={formik.values.birthdate}
                  onChange={(e) => {
                    formik.setFieldValue('birthdate', e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.birthdate && Boolean(formik.errors.birthdate)}
                  helperText={formik.touched.birthdate && formik.errors.birthdate}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  type='date'
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={12}
              >
                <TextField
                  fullWidth
                  label="Dirección completa"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </ScrollbarContainer>
        </CardContent>
        <CardActions
          sx={{
            display: 'flex',
            justifyContent: 'right',
            width: '100%',
            padding: 2,
          }}
        >
          <Button
            color="error"
            variant="outlined"
            onClick={formik.resetForm}
          >
            Limpiar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              if (formik.isValid) {
                formik.handleSubmit();
              } else {
                handleShowFormikErrors();
              }
            }}
          >
            Guardar
          </Button>
        </CardActions>
      </Card>

      <Card
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'end',
          height: '100%',
          width: '100%',
          maxWidth: '450px',
          flexDirection: 'column',
          borderRadius: '20px',
          marginTop: 4,
        }}
      >
        <CardHeader
          title={
            <Typography
              variant="h6"
              color="primary.black"
            >
              Buscar folio por nombre
            </Typography>
          }
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            flexDirection: 'row',
            padding: 2,
          }}
        >
          <TextField
            label="Buscar folio por nombre"
            name="nombre"
            value={nombre}
            onChange={(e) => {
              setNombre(e.target.value);
            }}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              marginRight: 2,
              width: '100%',
            }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setUsers([]);
              setNombre('');
            }}
            sx={{
              marginRight: 2,
            }}
          >
            Limpiar
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => getUserFolio()}
          >
            Buscar
          </Button>
        </Box>
      </Card >

      {
        users && users.length > 0 && (
          <Card
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'center',
              height: '100%',
              width: '100%',
              flexDirection: 'column',
              borderRadius: '20px',
              marginTop: 4,
            }}
          >
            <TableContainer
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
              }}
              component={Paper}
            >
              <Table
                sx={{
                  backgroundColor: 'white',
                }}
              >
                <TableHead
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: 'primary.white',
                    }}
                  >
                    Folio
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: 'primary.white',
                    }}
                  >
                    Nombre
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: 'primary.white',
                    }}
                  >
                    CURP
                  </TableCell>
                </TableHead>
                <TableBody>
                  {users?.map((user) => (
                    <TableRow
                      key={user?.id}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="primary.textDark"
                        >
                          {user?.folio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="primary.textDark"
                        >
                          {user?.nombre}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="primary.textDark"
                        >
                          {user?.curp}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card >
        )
      }

      <FolioDialog
        open={formSend}
        data={dataResponse}
        handleClose={() => setFormSend(false)}
      />
    </Box >
  );
};

const Preregistro = () => {
  return (
    <LayoutDashboard>
      <HomePage />
    </LayoutDashboard>
  );
}

export default Preregistro;
